import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { payersApi, type PayerDirectoryPatch } from '../api/payersApi';
import { payerKeys } from '../queryKeys';

// Agency Command Center's Insurance Payers page — browse + add/delete in the
// full directory (distinct from a practice's location-scoped payer selections).
export function useAllPayers() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: payerKeys.directoryAll(),
    queryFn: () => payersApi.listAllPayers(),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: payerKeys.directoryAll() });
    queryClient.invalidateQueries({ queryKey: payerKeys.directoryPrefix() });
  }

  // Two-step under the hood (create, then patch the rest) so the full
  // directory field set can be captured at creation time without a
  // dedicated "create with fields" backend endpoint — the practice-facing
  // name-only addPayerManually stays untouched for its own callers.
  const createPayer = useMutation({
    mutationFn: async (input: PayerDirectoryPatch & { name: string }) => {
      const created = await payersApi.addPayerManually(input.name);
      const { name: _name, ...rest } = input;
      void _name;
      return payersApi.updatePayerDirectoryEntry(created.id, rest);
    },
    onSuccess: invalidate,
  });

  const deletePayer = useMutation({
    mutationFn: (payerId: string) => payersApi.deletePayer(payerId),
    onSuccess: invalidate,
  });

  return { ...query, payers: query.data ?? [], createPayer, deletePayer, invalidate };
}
